import { queryOptions, useQuery } from "@tanstack/react-query";

import { NavigationElement, api, Dataset, Extranet, DatasetCapabilities } from "@/lib/api";
import { unwrapAxiosData } from "@/lib/api/query-utils";

import { queryKeys } from ".";

export const keys = {
  dashboardElements: (dashboardId: string) =>
    ["dashboard", dashboardId, "elements"] as const,
  extranets: () => ["extranets"] as const,
  datasets: (url: string) => ["datasets", url] as const,
  timerange: (dashboardId: string) => ["timerange", dashboardId],
};

export const dashboardNavigationElementsQuery = (id: string) =>
  queryOptions({
    queryKey: keys.dashboardElements(id),
    queryFn: async (): Promise<NavigationElement[]> =>
      (await api.navigationElements.getNavigationElementsForDashboard(id)).data,
  });

export const useDashboardNavigationElements = (dashboardId: string) =>
  useQuery(dashboardNavigationElementsQuery(dashboardId));

export const useDashboardNavigationElementFromPageQuery = (
  dashboardId: string,
  navigationElementId: string,
) => {
  return useQuery({
    ...dashboardNavigationElementsQuery(dashboardId),
    select: (data) => data.find((el) => el.page_id === navigationElementId),
  });
};

export const useExtranetsQuery = () => {
  return useQuery({
    queryKey: keys.extranets(),
    queryFn: (): Promise<Extranet[]> => unwrapAxiosData(api.extranets.getExtranets()),
  });
};

export const useExtranetDetailQuery = (id: string) => {
  return useQuery({
    queryKey: ["extranets", id],
    queryFn: (): Promise<Extranet> => unwrapAxiosData(api.extranets.getExtranet(id)),
  });
};

export const useCurrentExtranet = () => {
  return useQuery({
    queryKey: ["extranets", "current"],
    queryFn: (): Promise<Extranet> => unwrapAxiosData(api.extranets.getCurrentExtranet()),
  });
};

export const useDatasetsQuery = (type: string) => {
  return useQuery({
    queryKey: keys.datasets(type),
    queryFn: (): Promise<Dataset[]> => unwrapAxiosData(api.datasets.getDatasets(type)),
  });
};

export const useGetCapabilitiesQuery = (
  url: string,
  options?: { enabled: boolean; queryKey: string[] },
) => {
  return useQuery({
    queryKey: queryKeys.datasets(url),
    queryFn: (): Promise<DatasetCapabilities> =>
      unwrapAxiosData(api.datasets.getCapabilitiesForDataset(url)),
    ...options,
  });
};
